/**
 * Assigned to me — the reviewer's half of the review loop.
 *
 * Titled "My approvals" until 2026-09-02; see the rename note on
 * `messages.myApprovals`. The file, route and data-test ids keep the old
 * name deliberately — they are identifiers, and `/workflows/my-approvals` is
 * a live URL.
 *
 * Reviews assigned to *you* that you have not yet resolved. This is the
 * `tasks` category of `/notifications` promoted out of a filter chip into a
 * page of its own with a sidebar count, because a review you have been asked
 * to do is not an "activity to catch up on" — it is work someone is waiting
 * on, and it needs to be findable without first noticing a dot.
 *
 * Same data and same resolution verbs as the Notifications page: no new
 * endpoint, and `useAllNotificationsQuery` is already polled by the sidebar,
 * so opening this page costs no extra request. `useResolveTaskMutation`
 * prunes the resolved row from the shared cache, so the count in the rail
 * clears in lock-step with the row leaving the list.
 *
 * Deliberately still here rather than replaced: the Notifications page keeps
 * its Tasks filter. Two doors onto the same list is better than moving a
 * working surface out from under people, and the duplication is cheap — both
 * read one cache. Whether tasks should leave Notifications entirely is a
 * product call, not a technical one, and is still open.
 *
 * See `docs/design/workflows-section.md`.
 */
import * as React from 'react';
import cn from 'classnames';
import moment from 'moment';
import { useHistory } from 'react-router-dom';

import { Button, Loader, PageBody, PageTitle } from 'src/components';
import { Checks } from 'src/common/icons';
import { TNotification, TTaskAction } from 'src/types/TNotification';
import { useAllNotificationsQuery } from 'src/api/queries/notificationQueries';
import { useResolveTaskMutation } from 'src/api/mutations/notificationMutations';
import { config } from 'src/utils/config';
import { openNotificationUrl, taskSubject } from 'src/utils/notificationLink';
import { INTENT_LABEL } from './reviewRows';

import styles from './styles.module.scss';

type ApprovalRowProps = {
  notification: TNotification;
  onOpen: (notification: TNotification) => void;
  onResolve: (notification: TNotification, action: TTaskAction) => void;
  busy: boolean;
};

/**
 * One row, deliberately the same table as Reviews rather than the card list
 * this page used to render. The two pages are one loop seen from both ends, so
 * a reader moving between them should not have to re-learn the layout — and
 * every sibling list in the rail (Reviews, Scheduled agents, Shares, Jobs) is
 * a full-width table. `SchedulesList` is the precedent for the actions cell.
 */
const ApprovalRow: React.FC<ApprovalRowProps> = ({ notification, onOpen, onResolve, busy }) => {
  const subject = taskSubject(notification.message);
  const canOpen = Boolean(notification.url);
  const intent = notification.intent ? INTENT_LABEL[notification.intent] : null;

  const due = notification.due_on ? moment(notification.due_on) : null;
  const overdue = Boolean(due && due.isBefore(moment()));

  // Buttons live inside a clickable row, so every one of them has to stop the
  // click from also opening the review — the same guard `SchedulesList` needs
  // for its row-level Resume/Pause.
  const act = (action: TTaskAction) => (e: React.MouseEvent) => {
    e.stopPropagation();
    onResolve(notification, action);
  };

  return (
    <tr
      className={cn({ [styles.rowHot]: overdue })}
      onClick={canOpen ? () => onOpen(notification) : undefined}
      data-test="approval-item"
    >
      <td>
        <span className={styles.rowTitle}>{subject}</span>
        <span className={styles.rowDetail} title={moment(notification.timestamp).format('LLL')}>
          {moment(notification.timestamp).fromNow()}
        </span>
      </td>
      <td>
        {/* The ask gets its own column because it changes what the reviewer
            does before they read what it is about: "Fix & re-upload" and
            "Take a look" want completely different attention. As a card this
            had to lead the title to earn that prominence; a column gives it
            structurally. */}
        {intent ? <span className={styles.ask}>{intent}</span> : <span className={styles.due}>—</span>}
      </td>
      <td>
        {notification.assigned_by_email ? (
          <span className={styles.reviewer} title={notification.assigned_by_email}>
            {notification.assigned_by_email}
          </span>
        ) : (
          <span className={styles.due}>—</span>
        )}
      </td>
      <td>
        {due ? (
          <span
            className={cn(styles.due, { [styles.dueOverdue]: overdue })}
            title={due.format('LLL')}
          >
            {overdue ? `overdue ${due.fromNow(true)}` : `due ${due.fromNow()}`}
          </span>
        ) : (
          <span className={styles.due}>—</span>
        )}
      </td>
      <td>
        <div className={styles.actionsCell}>
          <Button size="small" mode="tertiary" disabled={busy} onClick={act('decline')}>
            Decline
          </Button>
          <Button size="small" mode="accept" disabled={busy} onClick={act('complete')}>
            Mark done
          </Button>
          {canOpen && (
            <Button
              size="small"
              mode="primary"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onOpen(notification);
              }}
            >
              Open &amp; review
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export const MyApprovals: React.FC = () => {
  const history = useHistory();
  const resolveTaskMutation = useResolveTaskMutation();

  const { isLoading, data } = useAllNotificationsQuery(undefined, {
    refetchIntervalInBackground: true,
    refetchInterval: config.notificationsRefetchInterval,
  });

  const notifications: TNotification[] = data?.notifications ?? [];

  // Only unresolved reviews. A resolved task stays in the payload (the
  // backend keeps it until its disposal fires) but has no business on a
  // "waiting on you" page.
  const open = React.useMemo(
    () => notifications.filter((n) => n.category === 'tasks' && n.status === 'OPEN'),
    [notifications],
  );

  // Due soonest first, undated last (an undated review has no claim on
  // attention ahead of one with a deadline), then oldest-first within each
  // group — the one someone has been waiting on longest is the one to do next.
  const ordered = React.useMemo(
    () =>
      [...open].sort((a, b) => {
        if (a.due_on && b.due_on) {
          return new Date(a.due_on).getTime() - new Date(b.due_on).getTime();
        }
        if (a.due_on) return -1;
        if (b.due_on) return 1;
        return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
      }),
    [open],
  );

  const handleOpen = (notification: TNotification) =>
    openNotificationUrl(history, notification.url);

  const handleResolve = (notification: TNotification, action: TTaskAction) => {
    resolveTaskMutation.mutateAsync({ id: notification.id, action });
  };

  return (
    <div className={styles.page} data-test="my-approvals-page-content">
      <PageTitle title="Assigned to me" counter={ordered.length} className={styles.pageTitle} />
      {/* Full width, not `narrow` — Reviews is full width and these two are
          the same loop from opposite ends. */}
      <PageBody>
        <p className={styles.pageSub}>Reviews waiting on you.</p>

        {isLoading && (
          <div className={styles.loading}>
            <Loader />
            <p>Loading reviews...</p>
          </div>
        )}

        {!isLoading && ordered.length === 0 && (
          <div className={styles.empty} data-test="my-approvals-empty">
            <span className={styles.emptyIcon} aria-hidden>
              <Checks />
            </span>
            <p className={styles.emptyTitle}>Nothing waiting on you</p>
            <p className={styles.emptyBody}>
              When a teammate sends you assets to check, the review lands here and the count
              appears in the sidebar.
            </p>
          </div>
        )}

        {ordered.length > 0 && (
          <div className={styles.tableScroll}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Review</th>
                  <th>Ask</th>
                  <th>From</th>
                  <th>Due</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ordered.map((notification) => (
                  <ApprovalRow
                    key={notification.id}
                    notification={notification}
                    onOpen={handleOpen}
                    onResolve={handleResolve}
                    busy={resolveTaskMutation.isLoading}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageBody>
    </div>
  );
};

export default MyApprovals;
